import { InsertDriveFile, Upload } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';
import { FC } from 'react';
import { useDocuments } from './hooks';
import {
  ConfirmationModal,
  FilePreview,
  PatientFileUploadModal,
} from '@components';
import { paths } from '@types';
import { DocumentsListItem } from './components';

interface DocumentsProps {
  disabled: boolean;
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
}

export const Documents: FC<DocumentsProps> = ({ disabled, task }) => {
  const {
    isFileUploadModalOpen,
    handelOpenFileUploadModal,
    handelCloseFileUploadModal,
    handleDeleteFile,
    isDeleting,
    isConfirmModalOpen,
    handleCloseConfirmModal,
    handleOpenConfirmModal,
    handleOpenPreviewFileModal,
    handleClosePreviewFileModal,
    isPreviewModalOpen,
    previewFile,
    isFileDownloading,
    selectedFile,
  } = useDocuments();

  return (
    <>
      {task.documents.length === 0 ? (
        <Box
          sx={{
            height: 'calc(100vh - 310px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <InsertDriveFile
            sx={{ width: 56, height: 56, mb: 2 }}
            color='primary'
          />
          <Typography variant='h6'>
            {t('worklist.worklistItem.documents.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('worklist.worklistItem.documents.noResults.subTitle')}
          </Typography>
          <Button
            variant='outlined'
            startIcon={<Upload />}
            onClick={handelOpenFileUploadModal}
            disabled={disabled}
            sx={{ mt: 2 }}
          >
            {t('worklist.worklistItem.documents.uploadFile')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {t('worklist.worklistItem.documents.helperText')}
          </Typography>
          <Button
            variant='outlined'
            startIcon={<Upload />}
            onClick={handelOpenFileUploadModal}
            disabled={disabled}
            sx={{ mb: 3, width: 143 }}
          >
            {t('worklist.worklistItem.documents.uploadFile')}
          </Button>
          <Typography variant='subtitle2'>
            {t('worklist.worklistItem.documents.listTitle')}
          </Typography>
          <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            {task.documents.map((document, idx) => {
              return (
                <DocumentsListItem
                  key={document.id}
                  disabled={disabled}
                  id={document.id}
                  fileSize={document.file.size}
                  fileName={document.file.file_name}
                  lastItem={idx === task.documents.length - 1}
                  deleteAction={id => handleOpenConfirmModal(id)}
                  handleOpenPreviewFileModal={() =>
                    handleOpenPreviewFileModal({
                      ...document.file,
                      file_format: document.file.file_mime_type,
                      id: document.id,
                      size: document.file.size.toString(),
                    })
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}
      <PatientFileUploadModal
        isOpen={isFileUploadModalOpen}
        handleClose={handelCloseFileUploadModal}
        patient_id={task.patient.id}
        task_id={task.id}
      />
      <ConfirmationModal
        title={t('worklist.worklistItem.documents.confirmModal.title')}
        message={t('worklist.worklistItem.documents.confirmModal.message')}
        onConfirm={handleDeleteFile}
        onClose={handleCloseConfirmModal}
        isOpen={isConfirmModalOpen}
        isLoading={isDeleting}
        confirmText={t('general.delete')}
      />
      <FilePreview
        isReadOnly={disabled}
        isOpen={isPreviewModalOpen}
        handleClose={handleClosePreviewFileModal}
        fileDetails={selectedFile}
        file={previewFile}
        isFileDownloading={isFileDownloading}
      />
    </>
  );
};
